import React, { useEffect, useState } from "react";

import { LoadingIcon as Loading, Modal, Typography } from "@packages/ds";
import { useIntl } from "react-intl";

import { Button } from "ds/Button";
import Checkbox from "ds/Checkbox";
import * as Radio from "ds/Radio";
import * as Sticker from "ds/Sticker";
import useDecodedParams from "Hooks/useDecodedParams";
import useSelectorWithUrlParams from "Hooks/useSelectorWithUrlParams";
import { objectKeys } from "Libs/utils";
import {
  loadingAddonsSelector,
  getAddons,
  updateAddons,
  addonsErrorSelector,
  addonsSelector,
  OrganizationAddon
} from "Reducers/organization/addons";
import { useAppSelector, useAppDispatch } from "Store/hooks";

import * as S from "./AddonModals.style";

type OrgSupportAddonModalProps = {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
};
export const OrgSupportAddonModal = ({
  isOpen,
  setIsOpen
}: OrgSupportAddonModalProps) => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const { organizationId } = useDecodedParams<{
    organizationId: string;
  }>();

  const addons = useSelectorWithUrlParams(addonsSelector);
  const currentAddons = addons?.current;

  const addonsError = useAppSelector(addonsErrorSelector);
  const isAddonsLoading = useAppSelector(loadingAddonsSelector);

  const [hasCompliedWithAgreement, setHasCompliedWithAgreement] =
    useState(false);

  type KeysOfObjOrNull<T> = T extends null ? never : keyof T;

  const currentSupportLevel =
    (objectKeys(currentAddons?.support_level)[0] as
      | KeysOfObjOrNull<OrganizationAddon["available"]["support_level"]>
      | undefined) ?? "";

  const [modalRadioSelection, setModalRadioSelection] =
    useState(currentSupportLevel);

  const daysRemaining: number =
    currentAddons?.support_level?.[currentSupportLevel] ?? 0;

  const currentText = intl.formatMessage({
    id: `organization.billing.modal.current`
  });

  useEffect(() => {
    if (organizationId && !addons) {
      dispatch(getAddons({ organizationId }));
    }
  }, [addons, dispatch, organizationId]);

  const updateAddonsHandler = async () => {
    const result = await dispatch(
      updateAddons({
        organizationId,
        payload: {
          support_level: modalRadioSelection
        }
      })
    );

    if (result?.type?.includes("fulfilled")) {
      setIsOpen(false);
      window.location.reload();
    }
  };

  const isSupportLevelAvailable = (value: string) =>
    value === currentSupportLevel ||
    (
      addons?.upgrades_available?.support_level as string[] | undefined
    )?.includes(value);

  const actionButton = isAddonsLoading ? (
    <S.LoadingActionWrapper>
      <Loading />
    </S.LoadingActionWrapper>
  ) : (
    <Button
      onClick={updateAddonsHandler}
      variant="primary"
      disabled={
        modalRadioSelection === currentSupportLevel || !hasCompliedWithAgreement
      }
    >
      {intl.formatMessage({
        id: isSupportLevelAvailable(modalRadioSelection)
          ? "organization.billing.upgrading"
          : "organization.billing.downgrading"
      })}
    </Button>
  );

  return (
    <Modal
      size="medium"
      isOpen={isOpen && !!currentAddons}
      onOpenChange={() => setIsOpen(prevState => !prevState)}
      handleClose={() => setIsOpen(false)}
    >
      <div>
        <Typography.Title tag="h3">
          {intl.formatMessage({
            id: "organization.billing.modal.org_support.title"
          })}
        </Typography.Title>
        <S.ModalIntroText size="small" weight="regular">
          {intl.formatMessage({
            id: "organization.billing.modal.org_support.description"
          })}
        </S.ModalIntroText>
        <S.RadioGroupWrapper>
          <Radio.Group
            value={modalRadioSelection}
            onValueChange={(value: typeof modalRadioSelection) =>
              setModalRadioSelection(value)
            }
          >
            {objectKeys(addons?.available.support_level)?.map(elt => {
              const isEnabled =
                isSupportLevelAvailable(elt) ||
                addons?.current.support_level?.[currentSupportLevel] === 0;

              return (
                <Radio.Input
                  key={`org-support-add-on-${elt}`}
                  id={`org-support-add-on-${elt}`}
                  value={elt}
                  disabled={!isEnabled}
                  label={
                    <Typography.Label htmlFor={`org-support-add-on-${elt}`}>
                      <S.TitleTagWrapper>
                        <S.RadioMainText
                          size="medium"
                          weight="regular"
                          disabled={!isEnabled}
                        >
                          {intl.formatMessage({
                            id: `organization.billing.modal.org_support.${elt}`
                          })}
                          {elt === currentSupportLevel && ` ${currentText}`}
                        </S.RadioMainText>
                      </S.TitleTagWrapper>
                      <S.RadioDetails enabled={isEnabled}>
                        {intl.formatMessage({
                          id: `organization.billing.modal.org_support.${elt}.detail`
                        })}
                      </S.RadioDetails>
                    </Typography.Label>
                  }
                />
              );
            })}
          </Radio.Group>
        </S.RadioGroupWrapper>

        {modalRadioSelection !== currentSupportLevel &&
          (addons?.available?.support_level?.[modalRadioSelection] ?? 0) >
            0 && (
            <>
              <S.ModalDivider />
              <Checkbox
                disabled={false}
                label={intl.formatMessage(
                  { id: "organization.billing.modal.org_support.comply" },
                  {
                    value:
                      addons?.available?.support_level?.[modalRadioSelection]
                  }
                )}
                checked={hasCompliedWithAgreement}
                onCheckedChanged={() =>
                  setHasCompliedWithAgreement(prevState => !prevState)
                }
                forId="org-support-add-on-comply"
              />
            </>
          )}
        {currentSupportLevel !== "standard" ? (
          <Typography.Label>
            {intl.formatMessage(
              { id: "organization.billing.modal.org_support.note" },
              { value: addons?.available?.support_level?.[modalRadioSelection] }
            )}
          </Typography.Label>
        ) : null}
        <S.ActionWrapper>
          {daysRemaining > 0 ? (
            <S.MtcNote>
              {intl.formatMessage(
                {
                  id: "organization.billing.modal.remaining"
                },
                {
                  days: daysRemaining
                }
              )}
            </S.MtcNote>
          ) : null}
          <S.ButtonWrapper>
            <Button
              analyticId="cancel"
              variant="secondary"
              onClick={() => setIsOpen(false)}
            >
              {intl.formatMessage({
                id: "cancel"
              })}
            </Button>
            {actionButton}
          </S.ButtonWrapper>
        </S.ActionWrapper>
        {addonsError && (
          <S.StickerRoot priority="critical">
            <Sticker.Icon priority="critical" />
            <Sticker.Text>{addonsError}</Sticker.Text>
          </S.StickerRoot>
        )}
      </div>
    </Modal>
  );
};
